import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import ProjectVitalFinanceAccountDialog from '../components/ProjectVitalFinanceAccountDialog';
import ProjectVitalFinanceMovementDialog from '../components/ProjectVitalFinanceMovementDialog';
import ProjectVitalFinancePlanningSection from '../components/ProjectVitalFinancePlanningSection';
import ProjectVitalProfessionalCashflowSection from '../components/ProjectVitalProfessionalCashflowSection';
import ProjectVitalProfessionalControlSection from '../components/ProjectVitalProfessionalControlSection';
import ProjectVitalProfessionalInvoicesSection from '../components/ProjectVitalProfessionalInvoicesSection';
import ProjectVitalProfessionalReservesSection from '../components/ProjectVitalProfessionalReservesSection';
import { getPVProfessionalMonthlyControl } from '../services/projectVitalProfessionalControlService';
import {
  deleteProjectVitalFinanceMovement,
  getProjectVitalFinanceForecast,
  getProjectVitalFinanceHistory,
  getProjectVitalFinanceOverview,
  setProjectVitalFinanceAccountActive,
} from '../services/projectVitalService';
import type {
  ProjectVitalFinanceAccount,
  ProjectVitalFinanceMovement,
  ProjectVitalFinanceMovementType,
  ProjectVitalFinanceViewScope,
} from '../types/projectVital';

type FinanceSection =
  | 'summary'
  | 'movements'
  | 'invoices'
  | 'planning'
  | 'fiscality';

type MovementTypeFilter = 'todos' | ProjectVitalFinanceMovementType;

const CYAN = '#4DD0E1';
const GREEN = '#86efac';
const RED = '#fca5a5';
const AMBER = '#fcd34d';

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(value: string, delta: number) {
  const [year, month] = value.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLong(value: string) {
  const [year, month] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-ES', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month - 1, 1));
}

function formatMonthShort(value: string) {
  const [year, month] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-ES', {
    month: 'short',
  })
    .format(new Date(year, month - 1, 1))
    .replace('.', '');
}

function formatCurrency(value: number | string) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function metricTone(value: number) {
  if (value > 0) return GREEN;
  if (value < 0) return RED;
  return '#e2e8f0';
}

function SectionTabs({
  scope,
  section,
  onChange,
}: {
  scope: ProjectVitalFinanceViewScope;
  section: FinanceSection;
  onChange: (value: FinanceSection) => void;
}) {
  const items: Array<[FinanceSection, string]> = scope === 'professional'
    ? [
        ['summary', 'Resumen'],
        ['movements', 'Movimientos'],
        ['invoices', 'Facturas'],
        ['planning', 'Planificación'],
        ['fiscality', 'Fiscalidad'],
      ]
    : [
        ['summary', 'Resumen'],
        ['movements', 'Movimientos'],
        ['planning', 'Planificación'],
      ];

  return (
    <Stack
      direction="row"
      spacing={0.55}
      sx={{
        overflowX: 'auto',
        pb: 0.25,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {items.map(([value, label]) => {
        const selected = value === section;
        return (
          <Button
            key={value}
            size="small"
            onClick={() => onChange(value)}
            sx={{
              flexShrink: 0,
              minWidth: 0,
              px: { xs: .8, sm: 1.4 },
              py: { xs: .62, sm: .72 },
              borderRadius: { xs: 0, sm: 2.2 },
              textTransform: 'none',
              fontSize: '.78rem',
              fontWeight: 800,
              color: { xs: selected ? CYAN : '#64748b', sm: selected ? '#07111f' : '#94a3b8' },
              bgcolor: { xs: 'transparent', sm: selected ? CYAN : alpha('#ffffff', 0.025) },
              border: { xs: 'none', sm: `1px solid ${selected ? CYAN : alpha('#94a3b8', 0.10)}` },
              borderBottom: { xs: selected ? `2px solid ${CYAN}` : '2px solid transparent', sm: undefined },
              '&:hover': {
                bgcolor: { xs: alpha('#ffffff', .025), sm: selected ? '#88FFFF' : alpha('#ffffff', 0.045) },
              },
            }}
          >
            {label}
          </Button>
        );
      })}
    </Stack>
  );
}

function TinyMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#64748b', fontSize: '.69rem', fontWeight: 750 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.22,
          color: tone || '#f8fafc',
          fontSize: { xs: '.98rem', md: '1.08rem' },
          fontWeight: 900,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  strong = false,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: string;
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
      <Typography
        sx={{
          color: strong ? '#e2e8f0' : '#94a3b8',
          fontSize: strong ? '.82rem' : '.78rem',
          fontWeight: strong ? 800 : 550,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: tone || (strong ? '#f8fafc' : '#cbd5e1'),
          fontSize: strong ? '.9rem' : '.8rem',
          fontWeight: strong ? 900 : 800,
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function ProjectVitalFinancesView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [month, setMonth] = useState(currentMonth());
  const [scope, setScope] = useState<ProjectVitalFinanceViewScope>('personal');
  const [section, setSection] = useState<FinanceSection>('summary');

  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [newMovementType, setNewMovementType] = useState<ProjectVitalFinanceMovementType>('gasto');
  const [editingAccount, setEditingAccount] = useState<ProjectVitalFinanceAccount | null>(null);
  const [editingMovement, setEditingMovement] = useState<ProjectVitalFinanceMovement | null>(null);
  const [deleteMovement, setDeleteMovement] = useState<ProjectVitalFinanceMovement | null>(null);
  const [archiveAccount, setArchiveAccount] = useState<ProjectVitalFinanceAccount | null>(null);
  const [manageAccounts, setManageAccounts] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<MovementTypeFilter>('todos');
  const [accountFilter, setAccountFilter] = useState('todas');
  const [categoryFilter, setCategoryFilter] = useState('todas');

  useEffect(() => {
    setSection('summary');
    setManageAccounts(false);
    setShowAnalysis(false);
    setSearch('');
    setTypeFilter('todos');
    setAccountFilter('todas');
    setCategoryFilter('todas');
  }, [scope]);

  const overviewQuery = useQuery({
    queryKey: ['project-vital', 'finance', month, scope],
    queryFn: () => getProjectVitalFinanceOverview(month, scope),
  });

  const forecastQuery = useQuery({
    queryKey: ['project-vital', 'finance-forecast', 60, scope],
    queryFn: () => getProjectVitalFinanceForecast(60, scope),
  });

  const historyQuery = useQuery({
    queryKey: ['project-vital', 'finance-history', month, scope],
    queryFn: () => getProjectVitalFinanceHistory(month, 6, scope),
    enabled: section === 'movements' && showAnalysis,
  });

  const professionalControlQuery = useQuery({
    queryKey: ['project-vital', 'professional-monthly-control', month],
    queryFn: () => getPVProfessionalMonthlyControl(month),
    enabled: scope === 'professional',
  });

  useEffect(() => {
    if (!overviewQuery.dataUpdatedAt) return;
    queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-forecast'] });
    if (scope === 'professional') {
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-monthly-control'] });
    }
  }, [overviewQuery.dataUpdatedAt, queryClient, scope]);

  const overview = overviewQuery.data;
  const forecast = forecastQuery.data;
  const professionalControl = professionalControlQuery.data;
  const fiscal = professionalControl?.fiscal_estimate ?? null;

  const activeAccounts = useMemo(
    () => (overview?.accounts ?? []).filter((account) => account.is_active),
    [overview],
  );

  const archivedAccounts = useMemo(
    () => (overview?.accounts ?? []).filter((account) => !account.is_active),
    [overview],
  );

  const professionalAccounts = useMemo(
    () => activeAccounts.filter((account) => account.financial_scope === 'professional'),
    [activeAccounts],
  );

  const categories = useMemo(
    () => Array.from(new Set(
      (overview?.movements ?? [])
        .map((movement) => movement.category)
        .filter((value): value is string => Boolean(value)),
    )).sort((a, b) => a.localeCompare(b, 'es')),
    [overview],
  );

  const expenseByCategory = useMemo(() => {
    const totals = new Map<string, number>();
    for (const movement of overview?.movements ?? []) {
      if (movement.movement_type !== 'gasto') continue;
      const key = movement.category || 'Sin categoría';
      totals.set(key, (totals.get(key) ?? 0) + Number(movement.amount));
    }
    return Array.from(totals.entries())
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [overview]);

  const filteredMovements = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (overview?.movements ?? []).filter((movement) => {
      if (typeFilter !== 'todos' && movement.movement_type !== typeFilter) return false;
      if (
        accountFilter !== 'todas'
        && movement.account_id !== accountFilter
        && movement.source_account_id !== accountFilter
        && movement.destination_account_id !== accountFilter
      ) return false;
      if (categoryFilter !== 'todas' && movement.category !== categoryFilter) return false;
      if (!normalizedSearch) return true;

      return [
        movement.description,
        movement.category,
        movement.account_name,
        movement.source_account_name,
        movement.destination_account_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [overview, search, typeFilter, accountFilter, categoryFilter]);

  const recentMovements = useMemo(
    () => [...(overview?.movements ?? [])]
      .sort((a, b) => {
        const dateCompare = b.movement_date.localeCompare(a.movement_date);
        if (dateCompare !== 0) return dateCompare;
        return b.created_at.localeCompare(a.created_at);
      })
      .slice(0, 5),
    [overview],
  );

  const deleteMutation = useMutation({
    mutationFn: deleteProjectVitalFinanceMovement,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-history'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-forecast'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-monthly-control'] }),
      ]);
      enqueueSnackbar('Movimiento eliminado.', { variant: 'success' });
      setDeleteMovement(null);
    },
    onError: () => enqueueSnackbar('No se pudo eliminar el movimiento.', { variant: 'error' }),
  });

  const accountStatusMutation = useMutation({
    mutationFn: ({ accountId, isActive }: { accountId: string; isActive: boolean }) =>
      setProjectVitalFinanceAccountActive(accountId, isActive),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-forecast'] }),
      ]);
      enqueueSnackbar(variables.isActive ? 'Cuenta restaurada.' : 'Cuenta archivada.', { variant: 'success' });
      setArchiveAccount(null);
    },
    onError: (error: any) => enqueueSnackbar(
      error?.response?.data?.detail || 'No se pudo actualizar la cuenta.',
      { variant: 'error' },
    ),
  });

  const openNewMovement = (type: ProjectVitalFinanceMovementType = 'gasto') => {
    setEditingMovement(null);
    setNewMovementType(type);
    setMovementDialogOpen(true);
  };

  const openNewAccount = () => {
    setEditingAccount(null);
    setAccountDialogOpen(true);
  };

  if (overviewQuery.isLoading) {
    return (
      <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isProfessional = scope === 'professional';
  const protectedNeeded = isProfessional && fiscal
    ? Number(fiscal.additional_reserve_needed)
    : Number(professionalControl?.pending_reserves_total ?? 0);
  const estimatedAvailable = isProfessional
    ? fiscal
      ? Number(fiscal.estimated_available_professional)
      : Number(overview?.total_liquidity ?? 0) - protectedNeeded
    : Number(overview?.total_liquidity ?? 0);
  const availableDisplay = isProfessional && professionalControlQuery.isLoading
    ? 'Calculando…'
    : formatCurrency(estimatedAvailable);

  const monthlyIncome = Number(overview?.income ?? 0);
  const monthlyExpenses = Number(overview?.expenses ?? 0);
  const monthlyBalance = Number(overview?.balance ?? 0);
  const projectedNet60 = Number(forecast?.expected_income ?? 0) - Number(forecast?.expected_expenses ?? 0);

  return (
    <>
      <Stack spacing={1.55}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          spacing={1.25}
        >
          <Stack direction="row" spacing={0.55} sx={{ width: 'fit-content' }}>
            {([
              ['global', 'Global'],
              ['personal', 'Personal'],
              ['professional', 'Profesional'],
            ] as Array<[ProjectVitalFinanceViewScope, string]>).map(([value, label]) => {
              const selected = scope === value;
              return (
                <Button
                  key={value}
                  size="small"
                  onClick={() => setScope(value)}
                  sx={{
                    minWidth: 0,
                    px: { xs: 1.15, sm: 1.55 },
                    py: 0.72,
                    borderRadius: 2.2,
                    textTransform: 'none',
                    color: selected ? '#07111f' : '#94a3b8',
                    bgcolor: selected ? CYAN : alpha('#ffffff', 0.025),
                    fontWeight: 850,
                    fontSize: '.78rem',
                    '&:hover': { bgcolor: selected ? '#88FFFF' : alpha('#ffffff', 0.045) },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8}>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                minHeight: 38,
                borderRadius: 2.2,
                border: `1px solid ${alpha('#94a3b8', 0.15)}`,
                bgcolor: alpha('#ffffff', 0.018),
                overflow: 'hidden',
              }}
            >
              <IconButton size="small" onClick={() => setMonth((value) => shiftMonth(value, -1))} sx={{ borderRadius: 0 }}>
                <ChevronLeftRoundedIcon fontSize="small" />
              </IconButton>
              <Button
                onClick={() => setMonth(currentMonth())}
                sx={{
                  minWidth: 150,
                  flex: 1,
                  color: '#e2e8f0',
                  textTransform: 'capitalize',
                  fontSize: '.77rem',
                  fontWeight: 800,
                  borderRadius: 0,
                }}
              >
                {formatMonthLong(month)}
              </Button>
              <IconButton size="small" onClick={() => setMonth((value) => shiftMonth(value, 1))} sx={{ borderRadius: 0 }}>
                <ChevronRightRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              disabled={scope === 'global'}
              onClick={() => openNewMovement('gasto')}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                minHeight: 38,
                bgcolor: CYAN,
                color: '#07111f',
                fontWeight: 850,
                textTransform: 'none',
                '&:hover': { bgcolor: '#88FFFF' },
              }}
            >
              Movimiento
            </Button>
          </Stack>
        </Stack>

        <SectionTabs scope={scope} section={section} onChange={setSection} />

        {section === 'summary' && (
          <Stack spacing={1.25}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.45, md: 1.9 },
                borderRadius: { xs: 3.3, md: 3 },
                bgcolor: '#101d31',
                border: { xs: 'none', md: `1px solid ${alpha(CYAN, 0.15)}` },
                boxShadow: { xs: '0 10px 30px rgba(0,0,0,.16)', md: 'none' },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(250px, .95fr) minmax(0, 1.4fr)' },
                  gap: { xs: 1.4, lg: 2.2 },
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography sx={{ color: '#94a3b8', fontSize: '.72rem', fontWeight: 800 }}>
                    {isProfessional ? 'Disponible profesional estimado' : 'Disponible'}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.25,
                      color: estimatedAvailable >= 0 ? '#f8fafc' : RED,
                      fontSize: { xs: '2.05rem', md: '2.15rem' },
                      lineHeight: 1.1,
                      fontWeight: 950,
                      letterSpacing: '-.8px',
                    }}
                  >
                    {availableDisplay}
                  </Typography>
                  {isProfessional ? (
                    <Stack direction="row" spacing={2.2} sx={{ mt: 1.1 }}>
                      <TinyMetric label="En cuentas" value={formatCurrency(overview?.total_liquidity ?? 0)} />
                      <TinyMetric
                        label={fiscal ? 'Falta proteger' : 'Reservado'}
                        value={formatCurrency(protectedNeeded)}
                        tone={protectedNeeded > 0 ? AMBER : GREEN}
                      />
                    </Stack>
                  ) : (
                    <Typography sx={{ mt: .65, color: '#64748b', fontSize: '.75rem' }}>
                      Saldo actual de las cuentas activas del ámbito seleccionado.
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 1,
                    p: { xs: 0, lg: 1.35 },
                    borderRadius: 2.5,
                    bgcolor: { lg: alpha('#ffffff', 0.018) },
                  }}
                >
                  <TinyMetric label="Ingresos del mes" value={formatCurrency(monthlyIncome)} tone={GREEN} />
                  <TinyMetric label="Gastos del mes" value={formatCurrency(monthlyExpenses)} tone={monthlyExpenses > 0 ? RED : '#e2e8f0'} />
                  <TinyMetric label="Balance del mes" value={formatCurrency(monthlyBalance)} tone={metricTone(monthlyBalance)} />
                </Box>
              </Box>
            </Paper>

            <Box sx={{ display: { xs: 'block', md: 'none' }, py: .1 }}>
              <Stack direction="row" justifyContent="space-between" spacing={.5}>
                {[
                  {
                    label: 'Movimiento',
                    icon: <AddRoundedIcon />,
                    action: () => openNewMovement('gasto'),
                    primary: true,
                  },
                  {
                    label: 'Transferir',
                    icon: <SwapHorizRoundedIcon />,
                    action: () => openNewMovement('transferencia'),
                  },
                  isProfessional
                    ? {
                        label: 'Facturas',
                        icon: <ReceiptLongRoundedIcon />,
                        action: () => setSection('invoices'),
                      }
                    : {
                        label: 'Cuentas',
                        icon: <AccountBalanceRoundedIcon />,
                        action: () => setManageAccounts(true),
                      },
                  {
                    label: 'Planificar',
                    icon: <EventNoteRoundedIcon />,
                    action: () => setSection('planning'),
                  },
                ].map((item) => (
                  <ButtonBase
                    key={item.label}
                    onClick={item.action}
                    disabled={scope === 'global' && (item.label === 'Movimiento' || item.label === 'Transferir')}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      py: .45,
                      borderRadius: 2.4,
                      opacity: scope === 'global' && (item.label === 'Movimiento' || item.label === 'Transferir') ? .35 : 1,
                    }}
                  >
                    <Stack alignItems="center" spacing={.55} sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: item.primary ? CYAN : alpha('#ffffff', .055),
                          color: item.primary ? '#07111f' : '#cbd5e1',
                          '& svg': { fontSize: 20 },
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        noWrap
                        sx={{ color: '#94a3b8', fontSize: '.65rem', fontWeight: 750, maxWidth: '100%' }}
                      >
                        {item.label}
                      </Typography>
                    </Stack>
                  </ButtonBase>
                ))}
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
                gap: 1.15,
              }}
            >
              <Paper
                elevation={0}
                sx={{ p: { xs: 1.35, md: 1.55 }, borderRadius: 2.8, bgcolor: { xs: alpha('#ffffff', .018), md: '#111c2f' }, border: { xs: 'none', md: `1px solid ${alpha('#94a3b8', .10)}` } }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '.9rem' }}>
                    Próximamente
                  </Typography>
                  <Button size="small" onClick={() => setSection('planning')} sx={{ textTransform: 'none', fontSize: '.72rem' }}>
                    Ver planificación
                  </Button>
                </Stack>
                <Stack spacing={.7}>
                  {isProfessional && professionalControl ? (
                    <>
                      <SummaryRow label="Por cobrar" value={formatCurrency(professionalControl.current_receivables)} />
                      <SummaryRow label="Por pagar" value={formatCurrency(professionalControl.current_payables)} />
                      <SummaryRow
                        label={fiscal ? 'Falta proteger ahora' : 'Reservado'}
                        value={formatCurrency(protectedNeeded)}
                        tone={protectedNeeded > 0 ? AMBER : GREEN}
                      />

                      {fiscal && fiscal.projection_suggestion_level !== 'none' ? (
                        <SummaryRow
                          label="Revisar previsión anual"
                          value={formatCurrency(fiscal.dynamic_projected_annual_revenue)}
                          tone={AMBER}
                        />
                      ) : null}

                    </>
                  ) : null}
                  <SummaryRow
                    label="Previsión neta próximos 60 días"
                    value={`${projectedNet60 >= 0 ? '+' : ''}${formatCurrency(projectedNet60)}`}
                    tone={metricTone(projectedNet60)}
                  />
                  <SummaryRow label="Movimientos previstos" value={String(forecast?.upcoming?.length ?? 0)} />
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: { xs: 1.35, md: 1.55 }, borderRadius: 2.8, bgcolor: { xs: alpha('#ffffff', .018), md: '#111c2f' }, border: { xs: 'none', md: `1px solid ${alpha('#94a3b8', .10)}` } }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '.9rem' }}>
                    Cuentas
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setManageAccounts((value) => !value)}
                    startIcon={manageAccounts ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    sx={{ textTransform: 'none', fontSize: '.72rem' }}
                  >
                    Gestionar
                  </Button>
                </Stack>

                {activeAccounts.length === 0 ? (
                  <Typography sx={{ color: '#64748b', fontSize: '.8rem' }}>No hay cuentas activas.</Typography>
                ) : (
                  <Stack spacing={.65}>
                    {activeAccounts.map((account) => (
                      <Stack key={account.id} direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ color: '#cbd5e1', fontWeight: 750, fontSize: '.8rem' }}>
                            {account.name}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '.67rem' }}>
                            {account.financial_scope === 'professional' ? 'Profesional' : 'Personal'}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '.84rem', whiteSpace: 'nowrap' }}>
                          {formatCurrency(account.current_balance)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Collapse in={manageAccounts}>
                  <Divider sx={{ my: 1.15, borderColor: alpha('#94a3b8', .10) }} />
                  <Stack spacing={.7}>
                    {activeAccounts.map((account) => (
                      <Stack key={`manage-${account.id}`} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography noWrap sx={{ color: '#94a3b8', fontSize: '.76rem', minWidth: 0 }}>
                          {account.name}
                        </Typography>
                        <Stack direction="row" spacing={.2}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingAccount(account);
                              setAccountDialogOpen(true);
                            }}
                            aria-label="Editar cuenta"
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setArchiveAccount(account)} aria-label="Archivar cuenta">
                            <ArchiveRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    ))}
                    {archivedAccounts.map((account) => (
                      <Stack key={`archived-${account.id}`} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography noWrap sx={{ color: '#64748b', fontSize: '.75rem' }}>
                          {account.name} · archivada
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<RestoreRoundedIcon />}
                          disabled={accountStatusMutation.isPending}
                          onClick={() => accountStatusMutation.mutate({ accountId: account.id, isActive: true })}
                          sx={{ textTransform: 'none' }}
                        >
                          Restaurar
                        </Button>
                      </Stack>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddRoundedIcon />}
                      disabled={scope === 'global'}
                      onClick={openNewAccount}
                      sx={{ mt: .4, alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                      Nueva cuenta
                    </Button>
                  </Stack>
                </Collapse>
              </Paper>
            </Box>

            <Paper
              elevation={0}
              sx={{ p: { xs: 1.35, md: 1.55 }, borderRadius: 2.8, bgcolor: { xs: alpha('#ffffff', .018), md: '#111c2f' }, border: { xs: 'none', md: `1px solid ${alpha('#94a3b8', .10)}` } }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '.9rem' }}>
                  Últimos movimientos
                </Typography>
                <Button size="small" onClick={() => setSection('movements')} sx={{ textTransform: 'none', fontSize: '.72rem' }}>
                  Ver todos
                </Button>
              </Stack>

              {recentMovements.length === 0 ? (
                <Typography sx={{ color: '#64748b', fontSize: '.8rem' }}>Todavía no hay movimientos este mes.</Typography>
              ) : (
                <Stack spacing={.4}>
                  {recentMovements.map((movement) => {
                    const isExpense = movement.movement_type === 'gasto';
                    const isIncome = movement.movement_type === 'ingreso';
                    const label = movement.movement_type === 'transferencia'
                      ? `${movement.source_account_name || 'Cuenta'} → ${movement.destination_account_name || 'Cuenta'}`
                      : movement.description || movement.category || 'Movimiento';
                    return (
                      <Stack
                        key={movement.id}
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ py: .55, borderBottom: `1px solid ${alpha('#94a3b8', .055)}` }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ color: '#cbd5e1', fontWeight: 750, fontSize: '.79rem' }}>
                            {label}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '.67rem' }}>
                            {formatDate(movement.movement_date)}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            color: isExpense ? RED : isIncome ? GREEN : '#cbd5e1',
                            fontWeight: 900,
                            fontSize: '.82rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isExpense ? '−' : isIncome ? '+' : ''}{formatCurrency(movement.amount)}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>
        )}

        {section === 'movements' && (
          <Paper
            elevation={0}
            sx={{ borderRadius: 3, bgcolor: '#111c2f', border: `1px solid ${alpha('#94a3b8', .10)}`, overflow: 'hidden' }}
          >
            <Box sx={{ p: { xs: 1.4, md: 1.7 } }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1.1}>
                <Box>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '.95rem' }}>Movimientos</Typography>
                  <Typography sx={{ color: '#64748b', fontSize: '.72rem', mt: .2 }}>
                    {filteredMovements.length} mostrados · {formatMonthLong(month)}
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={.7}>
                  <TextField
                    size="small"
                    placeholder="Buscar"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    InputProps={{
                      startAdornment: <SearchRoundedIcon sx={{ mr: .7, color: '#64748b', fontSize: 18 }} />,
                    }}
                  />
                  <Button
                    size="small"
                    startIcon={<TimelineRoundedIcon />}
                    onClick={() => setShowAnalysis((value) => !value)}
                    sx={{ textTransform: 'none' }}
                  >
                    {showAnalysis ? 'Ocultar análisis' : 'Ver análisis'}
                  </Button>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={.55} sx={{ mt: 1.15, overflowX: 'auto', pb: .2 }}>
                {([
                  ['todos', 'Todos'],
                  ['gasto', 'Gastos'],
                  ['ingreso', 'Ingresos'],
                  ['transferencia', 'Transferencias'],
                ] as Array<[MovementTypeFilter, string]>).map(([value, label]) => (
                  <Button
                    key={value}
                    size="small"
                    onClick={() => setTypeFilter(value)}
                    sx={{
                      flexShrink: 0,
                      minWidth: 0,
                      px: 1.15,
                      borderRadius: 99,
                      textTransform: 'none',
                      fontSize: '.72rem',
                      color: typeFilter === value ? CYAN : '#94a3b8',
                      bgcolor: typeFilter === value ? alpha(CYAN, .10) : 'transparent',
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={.7} sx={{ mt: .8 }}>
                <TextField
                  select
                  size="small"
                  label="Cuenta"
                  value={accountFilter}
                  onChange={(event) => setAccountFilter(event.target.value)}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="todas">Todas</MenuItem>
                  {activeAccounts.map((account) => <MenuItem key={account.id} value={account.id}>{account.name}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Categoría"
                  value={categoryFilter}
                  onChange={(event) => setCategoryFilter(event.target.value)}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="todas">Todas</MenuItem>
                  {categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                </TextField>
              </Stack>
            </Box>

            <Collapse in={showAnalysis}>
              <Box
                sx={{
                  px: { xs: 1.4, md: 1.7 },
                  pb: 1.35,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) minmax(260px, .65fr)' },
                  gap: .85,
                }}
              >
                <Box sx={{ p: 1.2, borderRadius: 2.4, bgcolor: alpha('#ffffff', .018) }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '.7rem', fontWeight: 800, mb: .8 }}>
                    Últimos seis meses
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(6, 1fr)' },
                      gap: .8,
                    }}
                  >
                    {historyQuery.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      (historyQuery.data ?? []).map((item) => (
                        <Box key={item.month}>
                          <Typography sx={{ color: '#64748b', fontSize: '.68rem', textTransform: 'capitalize' }}>
                            {formatMonthShort(item.month)}
                          </Typography>
                          <Typography sx={{ color: GREEN, fontSize: '.76rem', fontWeight: 800 }}>+{formatCurrency(item.income)}</Typography>
                          <Typography sx={{ color: RED, fontSize: '.72rem', fontWeight: 750 }}>−{formatCurrency(item.expenses)}</Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>

                <Box sx={{ p: 1.2, borderRadius: 2.4, bgcolor: alpha('#ffffff', .018) }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '.7rem', fontWeight: 800, mb: .8 }}>
                    Gastos por categoría · mes
                  </Typography>
                  {expenseByCategory.length === 0 ? (
                    <Typography sx={{ color: '#64748b', fontSize: '.75rem' }}>Sin gastos este mes.</Typography>
                  ) : (
                    <Stack spacing={.55}>
                      {expenseByCategory.map((item) => (
                        <SummaryRow key={item.category} label={item.category} value={formatCurrency(item.amount)} />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            </Collapse>

            <Divider sx={{ borderColor: alpha('#94a3b8', .09) }} />

            <Stack spacing={0}>
              {filteredMovements.length === 0 ? (
                <Typography sx={{ p: 2, color: '#64748b', fontSize: '.82rem' }}>No hay movimientos con estos filtros.</Typography>
              ) : (
                filteredMovements.map((movement) => {
                  const isExpense = movement.movement_type === 'gasto';
                  const isIncome = movement.movement_type === 'ingreso';
                  const title = movement.movement_type === 'transferencia'
                    ? `${movement.source_account_name || 'Cuenta'} → ${movement.destination_account_name || 'Cuenta'}`
                    : movement.description || movement.category || 'Movimiento';
                  const subtitle = movement.movement_type === 'transferencia'
                    ? 'Transferencia entre cuentas propias'
                    : [movement.category, movement.account_name].filter(Boolean).join(' · ');
                  return (
                    <Box
                      key={movement.id}
                      sx={{
                        px: { xs: 1.4, md: 1.7 },
                        py: 1,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr auto', md: '105px minmax(0,1fr) 150px auto' },
                        gap: { xs: .45, md: 1.2 },
                        alignItems: 'center',
                        borderBottom: `1px solid ${alpha('#94a3b8', .055)}`,
                      }}
                    >
                      <Typography sx={{ color: '#64748b', fontSize: '.72rem' }}>{formatDate(movement.movement_date)}</Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ color: '#e2e8f0', fontWeight: 800, fontSize: '.82rem' }}>{title}</Typography>
                        <Typography noWrap sx={{ color: '#64748b', fontSize: '.68rem', mt: .12 }}>{subtitle}</Typography>
                      </Box>
                      <Typography
                        sx={{
                          color: isExpense ? RED : isIncome ? GREEN : '#cbd5e1',
                          fontWeight: 900,
                          fontSize: '.84rem',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          gridRow: { xs: '1 / 3', md: 'auto' },
                          gridColumn: { xs: '2 / 3', md: 'auto' },
                        }}
                      >
                        {isExpense ? '−' : isIncome ? '+' : ''}{formatCurrency(movement.amount)}
                      </Typography>
                      <Stack direction="row" spacing={.1} justifyContent="flex-end" sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <IconButton size="small" onClick={() => { setEditingMovement(movement); setMovementDialogOpen(true); }} aria-label="Editar movimiento">
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteMovement(movement)} aria-label="Eliminar movimiento">
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Stack direction="row" spacing={.2} sx={{ display: { xs: 'flex', md: 'none' }, mt: .25 }}>
                        <Button size="small" onClick={() => { setEditingMovement(movement); setMovementDialogOpen(true); }}>Editar</Button>
                        <Button size="small" color="error" onClick={() => setDeleteMovement(movement)}>Eliminar</Button>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Paper>
        )}

        {section === 'invoices' && isProfessional && (
          <ProjectVitalProfessionalInvoicesSection professionalAccounts={professionalAccounts} />
        )}

        {section === 'planning' && (
          <Stack spacing={1.2}>
            <ProjectVitalFinancePlanningSection scope={scope} />
            {isProfessional ? <ProjectVitalProfessionalCashflowSection /> : null}
          </Stack>
        )}

        {section === 'fiscality' && isProfessional && (
          <Stack spacing={1.2}>
            <Paper
              elevation={0}
              sx={{ p: 1.45, borderRadius: 2.7, bgcolor: '#111c2f', border: `1px solid ${alpha('#94a3b8', .10)}` }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ md: 'center' }}>
                <Box>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '.92rem' }}>Fiscalidad y reservas</Typography>
                  <Typography sx={{ color: '#64748b', fontSize: '.72rem', mt: .2 }}>
                    Proyección anual, reserva necesaria hoy y detalle de obligaciones estimadas.
                  </Typography>
                </Box>
                {fiscal ? (
                  <Stack direction="row" spacing={2.2}>
                    <TinyMetric label="Proyección anual" value={formatCurrency(fiscal.projected_annual_revenue)} />
                    <TinyMetric label="Falta proteger" value={formatCurrency(fiscal.additional_reserve_needed)} tone={AMBER} />
                  </Stack>
                ) : null}
              </Stack>
            </Paper>
            <ProjectVitalProfessionalControlSection fiscalOnly month={month} onMonthChange={setMonth} />
            <ProjectVitalProfessionalReservesSection />
          </Stack>
        )}
      </Stack>

      <ProjectVitalFinanceAccountDialog
        open={accountDialogOpen}
        financialScope={editingAccount?.financial_scope ?? (scope === 'professional' ? 'professional' : 'personal')}
        account={editingAccount}
        onClose={() => {
          setAccountDialogOpen(false);
          setEditingAccount(null);
        }}
      />

      <ProjectVitalFinanceMovementDialog
        open={movementDialogOpen}
        financialScope={editingMovement?.financial_scope ?? (scope === 'professional' ? 'professional' : 'personal')}
        movement={editingMovement}
        initialMovementType={newMovementType}
        onClose={() => {
          setMovementDialogOpen(false);
          setEditingMovement(null);
        }}
      />

      <Dialog open={Boolean(deleteMovement)} onClose={deleteMutation.isPending ? undefined : () => setDeleteMovement(null)}>
        <DialogTitle>Eliminar movimiento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            El movimiento se eliminará y los saldos se recalcularán automáticamente. Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMovement(null)} disabled={deleteMutation.isPending}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending || !deleteMovement}
            onClick={() => deleteMovement && deleteMutation.mutate(deleteMovement.id)}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(archiveAccount)} onClose={accountStatusMutation.isPending ? undefined : () => setArchiveAccount(null)}>
        <DialogTitle>Archivar cuenta</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La cuenta dejará de mostrarse como activa. Sus movimientos y el histórico se conservarán.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveAccount(null)} disabled={accountStatusMutation.isPending}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={accountStatusMutation.isPending || !archiveAccount}
            onClick={() => archiveAccount && accountStatusMutation.mutate({ accountId: archiveAccount.id, isActive: false })}
          >
            Archivar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
